import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { formatTimeAgo } from '../../utils/time';

interface RecentActivity {
  type: string;
  description: string;
  timestamp: string;
}

const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    activeUsers: 0,
    userBreakdown: {
      companyUsers: 0,
      jobSeekers: 0,
      platformAdmins: 0,
    },
    totalResumesProcessed: 0,
    activeSubscriptions: 0,
    subscriptionBreakdown: {
      starter: 0,
      pro: 0,
      trial: 0,
    },
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      console.log('🔍 Debug: Token found:', token ? 'Yes' : 'No');
      console.log('🔍 Debug: Token length:', token?.length || 0);
      console.log('🔍 Debug: Token preview:', token ? token.substring(0, 20) + '...' : 'No token');

      if (!token) {
        console.error('❌ No access token found in localStorage');
        throw new Error('No access token found');
      }

      const response = await apiService.get('/super-admin/dashboard/stats');

      console.log('🔍 Debug: Response status:', response.status);
      const data = response.data;
      console.log('🔍 Debug: API Response:', data);
      console.log('🔍 Debug: Recent Activity from API:', data.recentActivity);

      setStats({
        totalCompanies: data.totalCompanies || 0,
        totalUsers: data.totalUsers || 0,
        activeUsers: data.activeUsers || 0,
        userBreakdown: {
          companyUsers: data.userBreakdown?.companyUsers || 0,
          jobSeekers: data.userBreakdown?.jobSeekers || 0,
          platformAdmins: data.userBreakdown?.platformAdmins || 0,
        },
        totalResumesProcessed: data.totalResumesProcessed || 0,
        activeSubscriptions: data.activeSubscriptions || 0,
        subscriptionBreakdown: {
          starter: data.subscriptionBreakdown?.starter || 0,
          pro: data.subscriptionBreakdown?.pro || 0,
          trial: data.subscriptionBreakdown?.trial || 0,
        },
      });

      // Set real recent activity data
      setRecentActivity(data.recentActivity || []);

      console.log('🔍 Debug: Stats updated successfully');
      console.log('🔍 Debug: Recent Activity set to:', data.recentActivity);
    } catch (error) {
      console.error('❌ Failed to load dashboard stats:', error);
      // Fallback to zero stats if API fails
      setStats({
        totalCompanies: 0,
        totalUsers: 0,
        activeUsers: 0,
        userBreakdown: {
          companyUsers: 0,
          jobSeekers: 0,
          platformAdmins: 0,
        },
        activeSubscriptions: 0,
        subscriptionBreakdown: {
          starter: 0,
          pro: 0,
          trial: 0,
        },
        totalResumesProcessed: 0,
      });
      setRecentActivity([]);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (activityType: string) => {
    switch (activityType) {
      case 'company_registered':
        return 'bg-green-400';
      case 'job_seeker_registered':
        return 'bg-blue-400';
      case 'subscription_upgrade':
        return 'bg-purple-400';
      case 'payment_success':
        return 'bg-emerald-400';
      case 'payment_failed':
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  };

  const formatActivityDescription = (activity: RecentActivity) => {
    switch (activity.type) {
      case 'company_registered':
        return activity.description;
      case 'job_seeker_registered':
        return activity.description;
      case 'subscription_upgrade':
        return activity.description;
      case 'payment_success':
        return activity.description;
      case 'payment_failed':
        return activity.description;
      default:
        return activity.description;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">TalentKey.AI Platform Overview</h1>
      </div>

      {/* Stats Grid */}
      <div className="mb-8">
        {/* Row 1: Core Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 mb-3">Total Companies</p>
              <div className="flex items-center justify-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.totalCompanies}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 mb-3">Total Users</p>
              <div className="flex items-center justify-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 mb-3">Active Users</p>
              <div className="flex items-center justify-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.activeUsers}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Activity Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 mb-3">Resumes Processed</p>
              <div className="flex items-center justify-center space-x-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.totalResumesProcessed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 mb-3">Active Subscriptions</p>
              <div className="flex items-center justify-center space-x-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.activeSubscriptions}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

        {/* User Breakdown */}
        <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-5">
            User Breakdown
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <span className="text-gray-600">Company Users</span>
              <span className="text-lg font-bold text-blue-600">
                {stats.userBreakdown.companyUsers}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <span className="text-gray-600">Job Seekers</span>
              <span className="text-lg font-bold text-green-600">
                {stats.userBreakdown.jobSeekers}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <span className="text-gray-600">Platform Admins</span>
              <span className="text-lg font-bold text-purple-600">
                {stats.userBreakdown.platformAdmins}
              </span>
            </div>
          </div>
        </div>

        {/* Subscription Breakdown */}
        <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-5">
            Subscription Plan Breakdown
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
              <span className="text-gray-600">Starter</span>
              <span className="text-lg font-bold text-gray-900">
                {stats.subscriptionBreakdown.starter}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50">
              <span className="text-gray-600">Pro</span>
              <span className="text-lg font-bold text-blue-600">
                {stats.subscriptionBreakdown.pro}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-purple-50">
              <span className="text-gray-600">Trial</span>
              <span className="text-lg font-bold text-purple-600">
                {stats.subscriptionBreakdown.trial}
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <a href="/super-admin/companies" className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-900">Manage Companies</span>
            </a>

            <a href="/super-admin/finance" className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-900">Finance</span>
            </a>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center">
                  <div className={`w-2 h-2 ${getActivityIcon(activity.type)} rounded-full mr-3`}></div>
                  <span className="text-sm text-gray-600">{formatActivityDescription(activity)}</span>
                  <span className="text-xs text-gray-400 ml-auto">{formatTimeAgo(activity.timestamp)}</span>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500">No recent activity</p>
                <p className="text-xs text-gray-400 mt-1">Activity will appear here as users interact with the platform</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard; 
